import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ClearIcon from "@mui/icons-material/Clear";
import { useSearchParams } from "react-router-dom";
import { useTenant } from "../contexts/tenant-context";
import { useSnackbar } from "../contexts/snackbar-context";
import { useApiService } from "../services/apiService";
import {
  AuditLogDto,
  AuditLogSortField,
  useAuditLogService,
} from "../services/auditLogService";
import { useTenantManagementService } from "../services/tenantManagementService";
import { User } from "../types/user";
import AuditLogTable from "../components/admin/audit-log-table";
import SearchBar from "../components/common/search-bar";
import { SortDirection, SortState } from "../components/common/generic-table";

const GLOBAL_ADMIN_TENANT_ID = "__GLOBAL_ADMIN__";
const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_PAGE_SIZE = 25;
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

/** Document search audits use a fixed entityId — hide them from the table only. */
const isDocumentSearchAudit = (item: AuditLogDto) => item.entityId === "search";

/** UTC calendar date as yyyy-MM-dd — matches backend date-only (UTC day) filter bounds. */
const toUtcDateString = (date: Date = new Date()) => date.toISOString().slice(0, 10);

const AUDIT_ACTIONS = ["Create", "Update", "Delete", "Download", "Attach", "Detach", "View", "Export", "Upload"];
const AUDIT_ENTITY_TYPES = [
  "Document",
  "CustomFolder",
  "Component",
  "Airframe",
  "Tenant",
  "User",
  "JSONTemplate",
  "BugLog",
  "Operator",
  "TenantSettings",
  "ShopVisitReason",
  "TenantLogo",
  "MiniPackTemplate",
  "SubAssembly",
  "MiniPackGeneration",
  "BulkUpload",
];

type ActivityTenantOption = { tenantId: string; tenantName: string };

const parsePositiveInt = (value: string | null, fallback: number): number => {
  const parsed = parseInt(value ?? "", 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const filterSelectSx = {
  minWidth: { xs: "100%", md: 170 },
  "& .MuiInputBase-root": { color: "#fff" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
  "& .MuiInputLabel-root": { color: "#aaa" },
  "& .MuiSvgIcon-root": { color: "#aaa" },
};

const dateFieldSx = {
  minWidth: { xs: "100%", md: 170 },
  "& .MuiInputBase-root": { color: "#fff" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
  "& .MuiInputLabel-root": { color: "#aaa" },
  "& input::-webkit-calendar-picker-indicator": { filter: "invert(1)" },
};

const AuditLogPage: React.FC = () => {
  const { selectedTenantId, availableTenants, isGlobalAdmin, selectedTenant } = useTenant();
  const { showError } = useSnackbar();
  const { getAuditLogs } = useAuditLogService();
  const { getTenants } = useTenantManagementService();
  const { apiCall } = useApiService();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMountedRef = useRef(true);
  const loadRequestIdRef = useRef(0);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Invalidate any in-flight load so a late response cannot update state after unmount.
      loadRequestIdRef.current += 1;
    };
  }, []);

  const isActingAsGlobalAdmin =
    isGlobalAdmin && (!selectedTenantId || selectedTenantId === GLOBAL_ADMIN_TENANT_ID);

  const fallbackTenantOptions = useMemo<ActivityTenantOption[]>(
    () =>
      (availableTenants ?? [])
        .filter((t) => t.tenantId !== GLOBAL_ADMIN_TENANT_ID)
        .map((t) => ({ tenantId: t.tenantId, tenantName: t.tenantName })),
    [availableTenants]
  );

  const [tenantOptions, setTenantOptions] = useState<ActivityTenantOption[]>(fallbackTenantOptions);
  const [selectedLogTenantId, setSelectedLogTenantId] = useState<string>(
    () => searchParams.get("tenantId") || ""
  );

  const [searchInput, setSearchInput] = useState(searchParams.get("search") || "");
  const [debouncedSearch, setDebouncedSearch] = useState(searchInput);
  const [actionFilter, setActionFilter] = useState(searchParams.get("action") || "");
  const [entityTypeFilter, setEntityTypeFilter] = useState(searchParams.get("entityType") || "");
  const [dateFrom, setDateFrom] = useState(searchParams.get("dateFrom") || "");
  const [dateTo, setDateTo] = useState(searchParams.get("dateTo") || "");
  const [performedByUserId, setPerformedByUserId] = useState(searchParams.get("performedByUserId") || "");
  const [page, setPage] = useState(() => Math.max(0, parsePositiveInt(searchParams.get("page"), 1) - 1));
  const [pageSize, setPageSize] = useState(() => parsePositiveInt(searchParams.get("pageSize"), DEFAULT_PAGE_SIZE));
  const [sortState, setSortState] = useState<SortState>(() => ({
    columnId: searchParams.get("sortBy") || "Timestamp",
    direction: (searchParams.get("sortDir") as SortDirection) || "desc",
  }));

  const [items, setItems] = useState<AuditLogDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [tenantNameById, setTenantNameById] = useState<Record<string, string>>({});
  const [tenantUsers, setTenantUsers] = useState<User[]>([]);

  const today = toUtcDateString();
  const dateRangeError = (() => {
    if (dateFrom && dateFrom > today) {
      return "From date cannot be in the future.";
    }
    if (dateTo && dateTo > today) {
      return "To date cannot be in the future.";
    }
    if (dateFrom && dateTo && dateFrom > dateTo) {
      return "Start date must be on or before the end date.";
    }
    return null;
  })();

  // Debounce free-text search before it drives a request.
  useEffect(() => {
    const timeoutId = window.setTimeout(() => setDebouncedSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeoutId);
  }, [searchInput]);

  // Any filter change resets paging back to page 1.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, actionFilter, entityTypeFilter, dateFrom, dateTo, performedByUserId, selectedLogTenantId]);

  // Global Admin tenant picker — mirrors recent-activity-panel.tsx.
  useEffect(() => {
    if (!isActingAsGlobalAdmin) {
      setTenantOptions(fallbackTenantOptions);
      return;
    }

    let isMounted = true;

    const loadTenantOptions = async () => {
      try {
        const tenants = await getTenants();
        if (!isMounted) return;

        if (!tenants.length) {
          setTenantOptions(fallbackTenantOptions);
          return;
        }

        const mapped = tenants
          .filter((t) => typeof t.id === "string" && t.id.trim().length > 0)
          .map((t) => ({ tenantId: t.id, tenantName: t.name }));

        const unique = Array.from(new Map(mapped.map((t) => [t.tenantId, t])).values());
        setTenantOptions(unique);
        setSelectedLogTenantId((prev) =>
          unique.some((t) => t.tenantId === prev) ? prev : unique[0]?.tenantId ?? ""
        );
      } catch {
        if (!isMounted) return;
        setTenantOptions(fallbackTenantOptions);
      }
    };

    void loadTenantOptions();

    return () => {
      isMounted = false;
    };
  }, [isActingAsGlobalAdmin, getTenants, fallbackTenantOptions]);

  const effectiveTenantId = isActingAsGlobalAdmin
    ? selectedLogTenantId || undefined
    : selectedTenantId || undefined;
  const shouldLoadActivity = isActingAsGlobalAdmin
    ? Boolean(selectedLogTenantId)
    : Boolean(effectiveTenantId);

  const canFilterByUser = isActingAsGlobalAdmin || selectedTenant?.role === "Admin";

  // Tenant name lookup for the "Tenant" column, shown to Global Admins only.
  useEffect(() => {
    if (!isActingAsGlobalAdmin) {
      setTenantNameById({});
      return;
    }

    let isMounted = true;

    (async () => {
      try {
        const tenants = await getTenants();
        if (!isMounted) return;

        const map = tenants.reduce<Record<string, string>>((acc, tenant) => {
          if (tenant.id) acc[tenant.id] = tenant.name;
          return acc;
        }, {});
        setTenantNameById(map);
      } catch {
        if (isMounted) setTenantNameById({});
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [isActingAsGlobalAdmin, getTenants]);

  // Users for the "Performed By" filter — admins/global admins only.
  useEffect(() => {
    if (!canFilterByUser || !effectiveTenantId) {
      setTenantUsers([]);
      return;
    }

    let isMounted = true;

    (async () => {
      try {
        const headers = isActingAsGlobalAdmin ? { "X-Tenant-Id": effectiveTenantId } : undefined;
        const response = await apiCall<User[]>({
          method: "GET",
          endpoint: "/api/AppUsers",
          headers,
          requireAuth: true,
        });
        if (isMounted) setTenantUsers(response.data ?? []);
      } catch {
        // The "Performed By" filter is a convenience — failure to load users shouldn't block the page.
        if (isMounted) setTenantUsers([]);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [canFilterByUser, effectiveTenantId, isActingAsGlobalAdmin, apiCall]);

  const loadAuditLogs = useCallback(async () => {
    if (!shouldLoadActivity || dateRangeError) {
      setItems([]);
      setTotalCount(0);
      return;
    }

    const requestId = ++loadRequestIdRef.current;

    try {
      setIsLoading(true);
      setErrorMessage(null);

      const result = await getAuditLogs({
        page: page + 1,
        pageSize,
        search: debouncedSearch || undefined,
        action: actionFilter || undefined,
        entityType: entityTypeFilter || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        performedByUserId: canFilterByUser ? performedByUserId || undefined : undefined,
        sortBy: sortState.columnId as AuditLogSortField,
        sortDescending: sortState.direction === "desc",
        tenantId: effectiveTenantId,
        isGlobalAdminContext: isActingAsGlobalAdmin,
      });

      // Ignore stale responses from an older filter/page request.
      if (!isMountedRef.current || requestId !== loadRequestIdRef.current) return;

      // Display-only: drop document-search audit rows (entityId === "search").
      // Backend still returns them in totalCount, so pagination may look slightly high.
      setItems(result.items.filter((item) => !isDocumentSearchAudit(item)));
      setTotalCount(result.totalCount);
    } catch (error) {
      if (!isMountedRef.current || requestId !== loadRequestIdRef.current) return;

      const message = error instanceof Error ? error.message : "Unable to load audit logs.";
      setItems([]);
      setTotalCount(0);
      setErrorMessage(message);
      showError(message);
    } finally {
      if (isMountedRef.current && requestId === loadRequestIdRef.current) {
        setIsLoading(false);
        setHasLoadedOnce(true);
      }
    }
  }, [
    shouldLoadActivity,
    dateRangeError,
    getAuditLogs,
    page,
    pageSize,
    debouncedSearch,
    actionFilter,
    entityTypeFilter,
    dateFrom,
    dateTo,
    canFilterByUser,
    performedByUserId,
    sortState,
    effectiveTenantId,
    isActingAsGlobalAdmin,
    showError,
  ]);

  useEffect(() => {
    void loadAuditLogs();
  }, [loadAuditLogs]);

  // Keep the view bookmarkable/shareable and survivable across refreshes.
  useEffect(() => {
    const next = new URLSearchParams();
    next.set("page", String(page + 1));
    next.set("pageSize", String(pageSize));
    if (debouncedSearch) next.set("search", debouncedSearch);
    if (actionFilter) next.set("action", actionFilter);
    if (entityTypeFilter) next.set("entityType", entityTypeFilter);
    if (dateFrom) next.set("dateFrom", dateFrom);
    if (dateTo) next.set("dateTo", dateTo);
    if (performedByUserId) next.set("performedByUserId", performedByUserId);
    next.set("sortBy", sortState.columnId);
    next.set("sortDir", sortState.direction);
    if (isActingAsGlobalAdmin && selectedLogTenantId) next.set("tenantId", selectedLogTenantId);

    setSearchParams(next, { replace: true });
  }, [
    page,
    pageSize,
    debouncedSearch,
    actionFilter,
    entityTypeFilter,
    dateFrom,
    dateTo,
    performedByUserId,
    sortState,
    isActingAsGlobalAdmin,
    selectedLogTenantId,
    setSearchParams,
  ]);

  const handleSortChange = (columnId: string) => {
    setSortState((prev) =>
      prev.columnId === columnId
        ? { columnId, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { columnId, direction: "asc" }
    );
  };

  const hasActiveFilters = Boolean(
    debouncedSearch || actionFilter || entityTypeFilter || dateFrom || dateTo || performedByUserId
  );

  const resetFilters = () => {
    setSearchInput("");
    setActionFilter("");
    setEntityTypeFilter("");
    setDateFrom("");
    setDateTo("");
    setPerformedByUserId("");
  };

  const emptyMessage = hasActiveFilters
    ? "No audit logs match your filters."
    : "No audit logs found yet.";

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} color="#fff">
            Audit Log
          </Typography>
          <Typography variant="body2" color="#bbb" sx={{ mt: 0.5 }}>
            {isActingAsGlobalAdmin
              ? "Browse the full audit trail across tenants."
              : canFilterByUser
              ? "Browse the full audit trail for your tenant."
              : "Browse your own activity history."}
          </Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => void loadAuditLogs()}
          disabled={isLoading}
          sx={{
            color: "hsl(19 101% 51% / 1)",
            borderColor: "hsl(19 101% 51% / 1)",
            textTransform: "none",
            fontWeight: 700,
          }}
        >
          {isLoading ? "Refreshing..." : "Refresh"}
        </Button>
      </Box>

      <Paper sx={{ backgroundColor: "#111", border: "1px solid #333", color: "#fff", p: { xs: 1.5, md: 2 }, mb: 2 }}>
        <Stack spacing={1.5}>
          {isActingAsGlobalAdmin && tenantOptions.length > 0 && (
            <FormControl size="small" sx={filterSelectSx}>
              <InputLabel>Tenant</InputLabel>
              <Select
                label="Tenant"
                value={selectedLogTenantId}
                onChange={(e) => setSelectedLogTenantId(e.target.value as string)}
              >
                {tenantOptions.map((tenant) => (
                  <MenuItem key={tenant.tenantId} value={tenant.tenantId}>
                    {tenant.tenantName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Box sx={{ width: "100%" }}>
            <SearchBar
              placeholder="Search by details or performed by..."
              value={searchInput}
              onChange={setSearchInput}
            />
          </Box>

          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} flexWrap="wrap" useFlexGap>
            <FormControl size="small" sx={filterSelectSx}>
              <InputLabel>Action</InputLabel>
              <Select
                label="Action"
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value as string)}
              >
                <MenuItem value="">All</MenuItem>
                {AUDIT_ACTIONS.map((action) => (
                  <MenuItem key={action} value={action}>
                    {action}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={filterSelectSx}>
              <InputLabel>Module</InputLabel>
              <Select
                label="Module"
                value={entityTypeFilter}
                onChange={(e) => setEntityTypeFilter(e.target.value as string)}
              >
                <MenuItem value="">All</MenuItem>
                {AUDIT_ENTITY_TYPES.map((entityType) => (
                  <MenuItem key={entityType} value={entityType}>
                    {entityType}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              type="date"
              label="From"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              inputProps={{
                max: dateTo && dateTo < today ? dateTo : today,
              }}
              InputLabelProps={{ shrink: true }}
              sx={dateFieldSx}
            />

            <TextField
              size="small"
              type="date"
              label="To"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              inputProps={{
                min: dateFrom || undefined,
                max: today,
              }}
              InputLabelProps={{ shrink: true }}
              sx={dateFieldSx}
            />

            {canFilterByUser && tenantUsers.length > 0 && (
              <FormControl size="small" sx={filterSelectSx}>
                <InputLabel>Performed By</InputLabel>
                <Select
                  label="Performed By"
                  value={performedByUserId}
                  onChange={(e) => setPerformedByUserId(e.target.value as string)}
                >
                  <MenuItem value="">Everyone</MenuItem>
                  {tenantUsers.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.displayName || user.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Button
              variant="text"
              startIcon={<ClearIcon />}
              onClick={resetFilters}
              disabled={!hasActiveFilters}
              sx={{ color: "#bbb", textTransform: "none", alignSelf: { xs: "flex-start", md: "center" } }}
            >
              Clear filters
            </Button>
          </Stack>

          {dateRangeError && (
            <Alert severity="warning" sx={{ bgcolor: "#2a1d00", color: "#fff" }}>
              {dateRangeError}
            </Alert>
          )}
        </Stack>
      </Paper>

      {!shouldLoadActivity && (
        <Paper sx={{ backgroundColor: "#111", border: "1px solid #333", color: "#fff", p: 3 }}>
          <Typography variant="body2" color="#d0d0d0">
            {isActingAsGlobalAdmin
              ? "Select a tenant above to view its audit log."
              : "No tenant selected."}
          </Typography>
        </Paper>
      )}

      {shouldLoadActivity && errorMessage && (
        <Alert
          severity="error"
          sx={{ mb: 2, bgcolor: "#2a0d00", color: "#fff", border: "1px solid #ff5b12" }}
          action={
            <Button color="inherit" size="small" onClick={() => void loadAuditLogs()}>
              Retry
            </Button>
          }
        >
          {errorMessage}
        </Alert>
      )}

      {shouldLoadActivity && !errorMessage && !hasLoadedOnce && isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress sx={{ color: "hsl(19 101% 51% / 1)" }} />
        </Box>
      )}

      {shouldLoadActivity && !errorMessage && (hasLoadedOnce || !isLoading) && (
        <Paper sx={{ backgroundColor: "#111", border: "1px solid #333" }}>
          <AuditLogTable
            items={items}
            loading={isLoading}
            emptyMessage={emptyMessage}
            isGlobalAdminContext={isActingAsGlobalAdmin}
            tenantNameById={tenantNameById}
            sortState={sortState}
            onSortChange={handleSortChange}
          />
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_event, nextPage) => setPage(nextPage)}
            rowsPerPage={pageSize}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            onRowsPerPageChange={(event) => {
              setPageSize(parseInt(event.target.value, 10));
              setPage(0);
            }}
            sx={{
              color: "#fff",
              borderTop: "1px solid #333",
              "& .MuiTablePagination-selectIcon, & .MuiTablePagination-actions, & .MuiTablePagination-displayedRows": {
                color: "#fff",
              },
            }}
          />
        </Paper>
      )}
    </Container>
  );
};

export default AuditLogPage;
